import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  incrementSteps,
  resetExperienceStepValues,
} from "../../../../Infrastructure/States/educatorStepsSlice";
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  useMediaQuery,
   Typography,
} from "@mui/material";
import {
  StyledTextField,
} from '../../../../Infrastructure/Theme/styledComponents';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  ContinueButton,
  EduBlankTitle,
  educatorFlowFooterSx,
  Footer,
  FormBoxEdu,
  LoginButton,
  TopHeading,
  TopHeadingBox,
} from "../../styles";
import { signInWithCustomToken, sendEmailVerification, signInWithEmailAndPassword } from "firebase/auth";
import { ShowErrorToast, ShowSuccessToast } from "../../../Common/Toast/toast";
import useAuthentication from "../../../../Infrastructure/States/onAuthStateChange";
import { httpsCallable } from "firebase/functions";
import { auth, database, functions } from "../../../../Infrastructure/config";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../../Common/Loader/loader";
import { useAuthValue } from "../../../../Infrastructure/States/authContext";
import './style.css'
import { VerifyEmailStep } from "./verifyEmailStep";
import { get, ref, update } from "firebase/database";
import countries from '../../../../Infrastructure/Constants/countries.json';
import { EduAccFlowHeader } from "../../../Common/EduAccFlowHeader/eduAccFlowHeader";
import { trackEvent } from "../../../../Infrastructure/Utilities/analytics";
import AnalyticsConstants from "../../../../Infrastructure/Constants/AnalyticsContants";
import { UNAUTH_LOGS_CLIENT, USER_LOGS_CLIENT } from "../../../../Infrastructure/Constants/logsConstant";
import ERROR_CONSTANTS from "../../../../Infrastructure/Constants/errorConstants";
import { getTimezone } from "../../../../Infrastructure/Utilities/getTimezone";
import { emailSchema } from "../../../../Infrastructure/Utilities/emailValidator";
import { fetchUserData } from "../../../../Infrastructure/States/userDataSlice";
import * as amplitude from "@amplitude/analytics-browser";
import paths from "../../../../Infrastructure/Router/paths";
import { safeClientLogEvent } from "../../../../Infrastructure/Utilities/safeClientLogEvent";
import { createAccountStepSx } from "./Styles/createAccountStepStyles";

export const CreateAccountStep = ({ lastPageRoute, urlInfoObj }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch();
  const { setTimeActive } = useAuthValue();
  const { user, loading } = useAuthentication();
  const { title, subtitle, fromJob } = urlInfoObj
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(false);
  const ip = useSelector((state) => state.ip.address);
  const page = useSelector((state)=> state.route.currentRoute)
  const [showPassword, setShowPassword] = useState(true);
  const [showRePassword, setShowRePassword] = useState(true);
  const steps = useSelector((state) => state.educatorSteps.steps);
  const userData = useSelector((state) => state.userData.data);
  const email_verified = userData?.email_verified
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const handleClick = async () => {
    try {
      setIsLoading(true)
      const updateProfileStep = httpsCallable(functions, "updatestep");
      await updateProfileStep({ step: steps + 1, page, ip })
    } catch (error) {
      await safeClientLogEvent({
        ...USER_LOGS_CLIENT.STEP_UPDATE_FAILED,
        uid: null,
        source: "createAccountStep.jsx",
        emailInfo: null,
        metadata: { step: steps + 1 },
        error: error?.message || null
      });
    } finally {
      setIsLoading(false)
    }
  };
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      reEnterPassword: "",
      phoneNumber: "",
      countryCode: ""
    },
    validationSchema: Yup.object({
      firstName: isLoginMode ? Yup.string() : Yup.string().required("First Name"),
      lastName: isLoginMode ? Yup.string() : Yup.string().required("Last Name"),
      email: emailSchema,
      password: isLoginMode ? Yup.string().min(6, "Must be 6 characters").required("Password")
        :
        Yup.string()
          .min(6, "Must be 6 characters")
        .matches(
          /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/,
          "Password must contain a special character"
        )
          .required("Password"),
      reEnterPassword: isLoginMode ? Yup.string() : Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm Password."),
      phoneNumber: isLoginMode ? Yup.string().matches(/^[0-9]+$/, "Phone number must be digits only")
       :
      Yup.string().matches(/^[0-9]+$/, "Phone number must be digits only").required("Phone Number"),
      countryCode:  isLoginMode ? Yup.string() : Yup.string().required("Country"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        if (!formik.isValid) {
          return;
        }
        if (isLoginMode) {
          // **LOGIN FLOW**
          const { email, password } = values;
          const restrictedEmails = process.env.REACT_APP_ADMIN_EMAIL?.split(',').map(e => e.trim()) || [];
          
          if (restrictedEmails.includes(email)) {
            ShowErrorToast("Access denied. Admin is not authorized to log in.");
            setIsLoading(false)
            return;
          }
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;
          try {
            const userRef = ref(database, `users/${user.uid}`);
            await update(userRef, { email_verified: user.emailVerified });
            const snapshot = await get(userRef);
            const userData = snapshot.val();
            amplitude.setUserId(user.uid);
            const identifyObj = new amplitude.Identify().set("email", userData?.account?.email);
            amplitude.identify(identifyObj)
            const eventType = userData?.is_educator ? AnalyticsConstants.EDUCATOR_LOGIN_SUCCESS : AnalyticsConstants.LEARNER_LOGIN_SUCCESS;
            dispatch(fetchUserData(user.uid))
            // Track login events (if it fails, log but continue)
            try {
              trackEvent(eventType, {
                user_id: user.uid,
                email: user.email,
              });
            } catch (trackingError) {
              await safeClientLogEvent({
                ...USER_LOGS_CLIENT.TRACKING_EVENT_FAILED,
                uid: user.uid,
                source: "createAccountStep.jsx",
                emailInfo: null,
                metadata: null,
                error: trackingError?.message || null
              });
            }
          } catch (firebaseError) {
            await safeClientLogEvent({
              ...UNAUTH_LOGS_CLIENT.EDUCATOR_LOGIN_FAILED,
              uid: user.uid,
              source: "createAccountStep.jsx",
              metadata: { email },
              error: firebaseError?.message || null
            });
          }
          setIsLoading(false)
        } else {
          setIsLoading(true)
          const timezone = getTimezone()
          // **REGISTRATION FLOW**
          const { email, password, firstName, lastName, phoneNumber, countryCode } = values;
          const requestData = {
            email,
            password,
            firstName,
            lastName,
            countryCode,
            number: phoneNumber,
            isEducator: true,
            timezone,
            ip: ip ?? null,
            page
          };
          if(urlInfoObj.req){
            requestData.reqId = urlInfoObj.req
          }
          const restrictedEmails = process.env.REACT_APP_ADMIN_EMAIL?.split(',').map(e => e.trim()) || [];
          
          if (restrictedEmails.includes(email)) {
            ShowErrorToast("Access denied. Admin is not authorized to create account.");
            setIsLoading(false)
            return;
          }
          const createUser = httpsCallable(functions, "createaccount");
          let token = null;
          let uid = null;
          
          setIsLoading(true);
          
          // Call Firebase Function to create user & get custom token
          createUser(requestData)
            .then((response) => {
              const { token: resToken, uid: resUid, isSuccess, errorMessage } = response.data || {};
              if(!isSuccess){
                throw new Error(errorMessage);
              }
              token = resToken;
              uid = resUid;
            })
            .catch(async (error) => {
              ShowErrorToast(error.message || "Educator account creation failed.");
              setIsLoading(false);
              await safeClientLogEvent({
                ...UNAUTH_LOGS_CLIENT.EDUCATOR_REGISTRATION_FAILED,
                uid: null,
                source: "createAccountStep.jsx",
                metadata: { email },
                error: error?.message || null
              });
              throw error; // prevents .finally block from running sign-in
            })
            .finally(() => {
              if (!token) {
                setIsLoading(false);
                return;
              }

              signInWithCustomToken(auth, token)
                .then( async (userCredential) => {
                  ShowSuccessToast("Account created successfully! Please verify your email.");
                amplitude.setUserId(uid);
                const identifyObj = new amplitude.Identify().set("email", userData?.account?.email);
                amplitude.identify(identifyObj)
                  try {
                    trackEvent(AnalyticsConstants.EDUCATOR_REGISTRATION_SUCCESS, { user_id: uid });
                  } catch (error) {
                    await safeClientLogEvent({
                      ...UNAUTH_LOGS_CLIENT.TRACKING_EVENT_FAILED,
                      uid: uid,
                      source: "createAccountStep.jsx",
                      emailInfo: null,
                      metadata: null,
                      error: error?.message || null
                    });
                  }
                  const user = userCredential.user;
                    sendEmailVerification(user)
                      .then(async () => {
                        setTimeActive(true);
                      })
                      .catch(async (error) => {
                        await safeClientLogEvent({
                          ...USER_LOGS_CLIENT.EMAIL_VERIFICATION_FAILED,
                          uid: uid,
                          source: "CreateAccountStep.jsx",
                          metadata: { email },
                          error: error?.message || null
                        });
                        ShowErrorToast("Failed to send verification email. Please try again.");
                      })
                      .finally(() => {
                        setIsLoading(false);
                      });
                })
                .catch(async(error) => {
                  await safeClientLogEvent({
                    ...USER_LOGS_CLIENT.CUSTOM_TOKEN_SIGNIN_FAILED,
                    uid: uid,
                    source: "CreateAccountStep.jsx",
                    metadata: { email },
                    error: error?.message || null
                  });
                  ShowErrorToast("Educator Sign-in failed.");
                  setIsLoading(false);
                });
            });
        }
      } catch (error) {
        if (isLoginMode) {
          handleLoginErrors(error, values.email)
        } else {
          handleRegistrationErrors(error, values.email)
        }
        setIsLoading(false)
      }
    },
  });
  const handleCountryChange = (event, newValue) => {
    formik.setFieldValue('countryCode', newValue ? newValue.alpha_code : '');
  };
  const handleToggleRePasswordVisibility = () => {
    setShowRePassword(!showRePassword);
  };
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const handleRegistrationErrors = async (error, email) => {
    if (!error) {
      return;
    }
    let errorMessage = ERROR_CONSTANTS.GENERIC_ERROR_TOAST;
    switch (error.code) {
      case "auth/email-already-in-use":
        errorMessage = "Email already exists.";
        break;
      case "auth/weak-password":
        errorMessage = "Weak password. Please choose a stronger one.";
        break;
      case "auth/user-token-expired":
        errorMessage = "User session expired. Please log in again.";
        break;
      case "auth/too-many-requests":
        errorMessage =
          error?.error?.message || "Too many requests. Please try again later.";
        break;
      default:
        ShowErrorToast(error);
    }
     await safeClientLogEvent({
        ...UNAUTH_LOGS_CLIENT.EDUCATOR_REGISTRATION_FAILED,
        uid: null,
        source: "createAccountStep.jsx",
        metadata:  { email },
        error: errorMessage || null
      });
    ShowErrorToast(errorMessage);
  };
  const handleLoginErrors = async (error, email) => {
    if (!error) {
      return;
    }
    let errorMessage = ERROR_CONSTANTS.GENERIC_ERROR_TOAST;
    switch (error.code) {
      case "auth/invalid-login-credentials":
        errorMessage =
          "Invalid credentials. Please check your email or password and try again.";
        break;
      case "auth/wrong-password":
        errorMessage =
          "Invalid password. Please check your password and try again.";
        break;
      case "auth/user-not-found":
        errorMessage = "User not found! Please Sign Up.";
        break;
      case "auth/too-many-requests":
        errorMessage =
          error?.error?.message || "Too many requests. Please try again later.";
        break;
      default:
        ShowErrorToast(error);
    }
     await safeClientLogEvent({
        ...UNAUTH_LOGS_CLIENT.EDUCATOR_LOGIN_FAILED,
        uid: null,
        source: "createAccountStep.jsx",
        metadata: null,
        error: errorMessage || null
      });
    ShowErrorToast(errorMessage);
  };
  const handleExit = () => {
    try {
      dispatch(resetExperienceStepValues());
      navigate(lastPageRoute || paths.home.path)
    } catch (err) {
      // console.log(err)
    }
  };
  const handleLoginButton = () => {
    setIsLoginMode(!isLoginMode)
    formik.resetForm();
  }
  useEffect(() => {
    if (email_verified) {
      dispatch(incrementSteps())
    }
  }, [email_verified, dispatch])
  return (
    <>
      <EduAccFlowHeader handleExit={handleExit} steps={steps} />
      {isLoading || loading ? <Loader height="100vh"/> :
        <Box sx={createAccountStepSx.rootWrapper}>
          {!user ?
            <Box sx={createAccountStepSx.leftFixedBox}>
              <TopHeadingBox pl={1} >
                <TopHeading variant="" my={1} >
                  {isLoginMode ? "Login" : " Create Account"}
                </TopHeading>
                <LoginButton onClick={handleLoginButton} color={"primary"}>

                  {isLoginMode ? "New? Create An Account" : "Have an account ? Login"}
                </LoginButton>
              </TopHeadingBox>
            </Box>
            : ""
          }
          <Box component="form"  sx={createAccountStepSx.formWrapper} onSubmit={formik.handleSubmit} noValidate>
            <Box sx={createAccountStepSx.formInner}>
            {!user ? (
              <Box sx={createAccountStepSx.centerColumn}>
                <FormBoxEdu p={4}>
                  {!isLoginMode ? <>
                    <StyledTextField
                      name="firstName"
                      label={
                        formik.touched.firstName && Boolean(formik.errors.firstName)
                          ? `${formik.errors.firstName}`
                          : "First Name"
                      }
                      variant="standard"
                      type="text"
                      onChange={formik.handleChange}
                      value={formik.values.firstName}
                      error={
                        formik.touched.firstName && Boolean(formik.errors.firstName)
                      }
                      fullWidth
                    />
                    <StyledTextField
                      name="lastName"
                      sx={createAccountStepSx.textFieldSpacing}
                      label={
                        formik.touched.lastName && Boolean(formik.errors.lastName)
                          ? `${formik.errors.lastName}`
                          : "Last Name"
                      }
                      variant="standard"
                      type="text"
                      onChange={formik.handleChange}
                      value={formik.values.lastName}
                      error={
                        formik.touched.lastName && Boolean(formik.errors.lastName)
                      }
                      fullWidth
                    />
                  </> : ""}
                  <StyledTextField
                    name="email"
                    sx={createAccountStepSx.textFieldSpacing}
                    label={
                      formik.touched.email && Boolean(formik.errors.email)
                        ? `${formik.errors.email}`
                        : "Email"
                    }
                    variant="standard"
                    type="email"
                    onChange={formik.handleChange}
                    value={formik.values.email}
                    autoComplete="username"
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    fullWidth
                  />

                  <StyledTextField
                    name="password"
                    sx={createAccountStepSx.textFieldSpacing}
                    label={
                      formik.touched.password && Boolean(formik.errors.password)
                        ? formik.errors.password
                        : "Password"
                    }
                    variant="standard"
                    type={showPassword ? "password" : "text"}
                    onChange={formik.handleChange}
                    value={formik.values.password}
                    autoComplete="new-password"
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePasswordVisibility}>
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                  {!isLoginMode ? <>
                    <StyledTextField
                      name="reEnterPassword"
                      sx={createAccountStepSx.textFieldSpacing}
                      label={
                        formik.touched.reEnterPassword &&
                          formik.values.password !== formik.values.reEnterPassword
                          ? "Passwords do not match"
                          : "Confirm Password"
                      }
                      variant="standard"
                      type={showRePassword ? "password" : "text"}
                      onChange={formik.handleChange}
                      value={formik.values.reEnterPassword}
                      autoComplete="new-Password"
                      error={
                        formik.touched.reEnterPassword &&
                        formik.values.password !== formik.values.reEnterPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleToggleRePasswordVisibility}>
                              {showRePassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                    />
                    <StyledTextField
                      name="phoneNumber"
                      sx={createAccountStepSx.textFieldSpacing}
                      label={
                        formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
                          ? `${formik.errors.phoneNumber}`
                          : "Phone Number"
                      }
                      variant="standard"
                      onChange={formik.handleChange}
                      type="tel"
                      autoComplete="tel"
                      value={formik.values.phoneNumber}
                      error={
                        formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
                      }
                      InputProps={{
                        inputMode: "numeric",
                        onKeyPress: (event) => {
                          if (isNaN(event.key)) {
                            event.preventDefault();
                          }
                        },
                      }}
                      fullWidth
                    />
                    <Autocomplete
                      autoComplete
                      id="auto-complete"
                      options={countries}
                      value={countries.find(option => option.alpha_code === formik.values.countryCode) || null}
                      getOptionLabel={(option) => option.label}
                      onChange={handleCountryChange}
                      onFocus={() => {
                        if (formik.values.countryCode) {
                          formik.setFieldValue("countryCode", "");
                        }
                      }}                      
                      renderInput={(params) => <StyledTextField {...params}
                        label={
                          formik.touched.countryCode && Boolean(formik.errors.countryCode)
                            ? `${formik.errors.countryCode}`
                            : "Country"
                        }
                        variant="standard"
                        error={
                          formik.touched.countryCode && Boolean(formik.errors.countryCode)
                        }
                      />}
                      sx={createAccountStepSx.autocomplete}
                      fullWidth
                    />
                  </>
                    : ""
                  }
                </FormBoxEdu>
              </Box>
            ) : (
              <Box
                sx={createAccountStepSx.accountSubmittedTextBox}
              >
                {user?(
                email_verified ?(
                  <EduBlankTitle
                    variant={isSmallScreen ? "body2" : "body1"}
                    color="initial"
                    textAlign={"center"}
                    py={15}
                  >
                    Educator Account Created Successfully. Move to Next Step.
                  </EduBlankTitle>
                  ):(
                  <VerifyEmailStep />
                )
              ) : null}
              </Box>
            )}
          </Box>
            <Footer sx={educatorFlowFooterSx.footer}>
              {/* Mobile subtitle */}
              {fromJob && (
                <Typography
                  color="initial"
                  sx={educatorFlowFooterSx.mobileSubtitle}
                >
                  {title} - {subtitle}
                </Typography>
              )}
              <Box sx={educatorFlowFooterSx.footerRow}>
                {/* Empty left spacer on desktop */}
                <Box sx={educatorFlowFooterSx.footerSpacer}/>
                {/* Center subtitle (desktop only) */}
                {fromJob && (
                  <Box sx={educatorFlowFooterSx.footerCenter}>
                    <Typography fontWeight={600} color="initial" textAlign="center">
                      {title} - {subtitle}
                    </Typography>
                  </Box>
                )}
                {/* Continue button aligned right */}
                <Box sx={educatorFlowFooterSx.footerRight}>
                  <ContinueButton
                    disabled={(user && !email_verified)}
                    variant="contained"
                    type={!user ? "submit" : "button"}
                    onClick={user ? handleClick : undefined}
                  >
                    Continue
                  </ContinueButton>
                </Box>
              </Box>
            </Footer>
          </Box>
        </Box>
      }
    </>
  );
};
